/**
 * ApprovalModal — surfaces pending owner-approval requests for the
 * viewer. Pops up when an entry in `state.pendingApprovals` has a
 * `target_owner` matching the viewer. Approve / Deny dispatch a
 * `ResolveZoneEdit` action; the requester observes the outcome via
 * the event log.
 *
 * Sandbox scaffolding — real auto-triggering from cross-player drops
 * is a follow-up. For now, requests can be issued via UI button or
 * (later) by the realm-tile drop handler when the dropped target is
 * the opponent's pile.
 */

import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import type { PlayerId, PublicTableState, TableAction } from '../types.js';

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  dispatchAction: (a: TableAction) => void;
};

const describeZone = (
  z:
    | { kind: 'deck' | 'abyss' | 'banished'; owner: PlayerId }
    | { kind: 'realm'; realm: string; owner: PlayerId },
): string => {
  if (z.kind === 'realm') return `${z.realm} (realm)`;
  return z.kind;
};

const describeKind = (k: 'reorder' | 'inspect' | 'move-to'): string =>
  k === 'reorder' ? 'reorder' : k === 'inspect' ? 'inspect' : 'place a card into';

export function ApprovalModal({ state, viewer, dispatchAction }: Props): ReactElement | null {
  const map = state.pendingApprovals ?? {};
  const myRequests = Object.entries(map).filter(([, r]) => r.target_owner === viewer);
  if (myRequests.length === 0) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pending zone-edit requests"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-cosmos-950/80 backdrop-blur-sm"
    >
      <div className="max-h-[80vh] w-[90vw] max-w-md overflow-y-auto rounded-md border border-amber-700 bg-cosmos-900 p-4 shadow-2xl">
        <h2 className="mb-3 font-bold text-amber-200 text-lg">Approval needed</h2>
        <ul className="space-y-3">
          {myRequests.map(([id, r]) => (
            <li key={id} className="rounded border border-cosmos-700 bg-cosmos-800/60 p-3">
              <p className="text-cosmos-100 text-sm">
                <span className="font-semibold text-amber-300">{r.requester}</span> wants to{' '}
                <span className="text-amber-200">{describeKind(r.request_kind)}</span> your{' '}
                <span className="text-amber-200">{describeZone(r.target_zone)}</span>.
              </p>
              {r.note && <p className="mt-1 text-cosmos-400 text-xs italic">"{r.note}"</p>}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    dispatchAction({
                      kind: 'ResolveZoneEdit',
                      request_id: id,
                      decision: 'approve',
                    })
                  }
                  className="rounded bg-emerald-700 px-3 py-1 font-medium text-white text-xs hover:bg-emerald-600"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() =>
                    dispatchAction({
                      kind: 'ResolveZoneEdit',
                      request_id: id,
                      decision: 'deny',
                    })
                  }
                  className="rounded bg-rose-700 px-3 py-1 font-medium text-white text-xs hover:bg-rose-600"
                >
                  Deny
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
